import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { ArrowLeft, AlertTriangle, Globe2, Building2 } from 'lucide-react';
import { getBlastRadius, getInstitution } from '../api/client';
import type { AffectedEntity } from '../types';

interface GraphNode extends d3.SimulationNodeDatum {
  id: string;
  name: string;
  type: string;
  country: string;
  distance: number;
  isSource: boolean;
}

interface GraphLink extends d3.SimulationLinkDatum<GraphNode> {
  source: GraphNode;
  target: GraphNode;
}

function BlastRadiusGraph({ entities, sourceLei, sourceName }: { entities: AffectedEntity[]; sourceLei: string; sourceName?: string }) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || entities.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = svgRef.current.clientWidth;
    const height = 500;

    // Build nodes and links
    const nodesMap = new Map<string, GraphNode>();

    // Add source node first (it may not be in entities list)
    nodesMap.set(sourceLei, {
      id: sourceLei,
      name: sourceName || 'Source Institution',
      type: 'SOURCE',
      country: '',
      distance: 0,
      isSource: true,
    });

    // Add all entities from the path arrays and the entities themselves
    entities.forEach((entity) => {
      // Add the entity node
      if (!nodesMap.has(entity.lei)) {
        nodesMap.set(entity.lei, {
          id: entity.lei,
          name: entity.legal_name,
          type: entity.primary_type,
          country: entity.jurisdiction_country,
          distance: entity.distance,
          isSource: false,
        });
      }

      // Add intermediate nodes from the path (they might not be in entities)
      if (entity.relationship_path && entity.relationship_path.length > 0) {
        entity.relationship_path.forEach((pathLei, idx) => {
          if (!nodesMap.has(pathLei) && pathLei !== sourceLei) {
            nodesMap.set(pathLei, {
              id: pathLei,
              name: pathLei.substring(0, 8) + '...',
              type: 'INTERMEDIATE',
              country: '',
              distance: idx,
              isSource: false,
            });
          }
        });
      }
    });

    const nodes = Array.from(nodesMap.values());
    const links: GraphLink[] = [];
    const linkSet = new Set<string>();

    entities.forEach((entity) => {
      if (entity.relationship_path && entity.relationship_path.length > 1) {
        for (let i = 0; i < entity.relationship_path.length - 1; i++) {
          const sourceId = entity.relationship_path[i];
          const targetId = entity.relationship_path[i + 1];
          const linkKey = `${sourceId}-${targetId}`;

          // Avoid duplicate links
          if (!linkSet.has(linkKey)) {
            linkSet.add(linkKey);
            const source = nodesMap.get(sourceId);
            const target = nodesMap.get(targetId);
            if (source && target) {
              links.push({ source, target });
            }
          }
        }
      }
    });

    // Create simulation
    const simulation = d3
      .forceSimulation<GraphNode>(nodes)
      .force(
        'link',
        d3.forceLink<GraphNode, GraphLink>(links).id((d) => d.id).distance(80)
      )
      .force('charge', d3.forceManyBody().strength(-200))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(30));

    // Create container
    const g = svg.append('g');

    // Add zoom
    svg.call(
      d3.zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.3, 3])
        .on('zoom', (event) => {
          g.attr('transform', event.transform);
        }) as any
    );

    // Draw links
    const link = g
      .append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', '#cbd5e1')
      .attr('stroke-width', 1.5);

    // Draw nodes
    const node = g
      .append('g')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .call(
        d3.drag<SVGGElement, GraphNode>()
          .on('start', (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on('drag', (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on('end', (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          }) as any
      );

    // Node circles
    node
      .append('circle')
      .attr('r', (d) => (d.isSource ? 20 : 12))
      .attr('fill', (d) => {
        if (d.isSource) return '#2563eb';
        if (d.distance === 1) return '#f97316';
        if (d.distance === 2) return '#eab308';
        return '#22c55e';
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    // Node labels
    node
      .append('text')
      .text((d) => d.name.slice(0, 15) + (d.name.length > 15 ? '...' : ''))
      .attr('x', 0)
      .attr('y', 25)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#374151');

    // Update positions on tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d) => (d.source as GraphNode).x!)
        .attr('y1', (d) => (d.source as GraphNode).y!)
        .attr('x2', (d) => (d.target as GraphNode).x!)
        .attr('y2', (d) => (d.target as GraphNode).y!);

      node.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    return () => {
      simulation.stop();
    };
  }, [entities, sourceLei, sourceName]);

  return (
    <svg
      ref={svgRef}
      className="w-full h-[500px] bg-gray-50 rounded-lg"
    />
  );
}

export default function BlastRadius() {
  const { lei } = useParams<{ lei: string }>();

  const { data: institution, isLoading: institutionLoading } = useQuery({
    queryKey: ['institution', lei],
    queryFn: () => getInstitution(lei!),
    enabled: !!lei,
  });

  const { data: blastRadius, isLoading: blastLoading, error } = useQuery({
    queryKey: ['blast-radius', lei],
    queryFn: () => getBlastRadius(lei!),
    enabled: !!lei,
  });

  const isLoading = institutionLoading || blastLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !blastRadius) {
    return (
      <div className="card p-12 text-center">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Unable to Calculate Blast Radius
        </h2>
        <p className="text-gray-500 mb-4">
          The graph database may not be available or configured.
        </p>
        <Link to={`/institution/${lei}`} className="btn-primary inline-block">
          Back to Institution
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to={`/institution/${lei}`}
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {institution?.legal_name || 'Institution'}
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Blast Radius Analysis</h1>
        <p className="mt-2 text-gray-600">
          Entities affected if a data breach occurs at this institution
        </p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Affected Entities</p>
              <p className="text-xl font-semibold text-gray-900">
                {blastRadius.total_entities}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <Globe2 className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Countries Affected</p>
              <p className="text-xl font-semibold text-gray-900">
                {blastRadius.countries_affected.length}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Max Depth</p>
              <p className="text-xl font-semibold text-gray-900">
                {blastRadius.max_depth} levels
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div>
            <p className="text-sm text-gray-500">Impact Score</p>
            <p className="text-2xl font-bold text-gray-900">
              {blastRadius.estimated_impact_score.toFixed(0)}
            </p>
          </div>
        </div>
      </div>

      {/* Graph visualization */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Relationship Graph
        </h2>
        <BlastRadiusGraph
          entities={blastRadius.affected_entities}
          sourceLei={blastRadius.source_lei}
          sourceName={blastRadius.source_name || institution?.legal_name}
        />
        <div className="flex items-center justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-primary-600" />
            <span className="text-gray-600">Source</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-orange-500" />
            <span className="text-gray-600">1 hop</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-yellow-500" />
            <span className="text-gray-600">2 hops</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500" />
            <span className="text-gray-600">3+ hops</span>
          </div>
        </div>
      </div>

      {/* Affected entities list */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Affected Entities ({blastRadius.total_entities})
        </h2>
        <div className="divide-y divide-gray-100">
          {blastRadius.affected_entities.map((entity) => (
            <Link
              key={entity.lei}
              to={`/institution/${entity.lei}`}
              className="flex items-center gap-4 py-3 hover:bg-gray-50 -mx-4 px-4 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {entity.legal_name}
                </p>
                <p className="text-sm text-gray-500">
                  {entity.primary_type.replace(/_/g, ' ')} • {entity.jurisdiction_country}
                </p>
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-xs text-gray-600">
                  {entity.distance} {entity.distance === 1 ? 'hop' : 'hops'}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
